import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";
import {
	Loader2,
	Trash2,
	Plus,
	AlertTriangle,
	ListPlus,
	Users2,
} from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import Secao from "../WhatsAppModal/Secao";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { MultiSelect } from "../ui/multi-select";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import useWhatsApps from "../../hooks/useWhatsApps";

import { i18n } from "../../translate/i18n";

import PhoneInput from "../PhoneInput";
import KanbanEtapaChip from "../KanbanEtapaChip";
import usePaisFiscal from "../../hooks/usePaisFiscal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Muito curto")
		.max(50, "Muito longo")
		.required("Campo obrigatório"),
	number: Yup.string().min(8, "Muito curto").max(50, "Muito longo"),
	email: Yup.string().email("E-mail inválido"),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	// País padrão do seletor: numa operação portuguesa, abrir em Brasil faria
	// a equipe trocar o país em todo contato novo.
	const regras = usePaisFiscal();
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		email: "",
		disableBot: false,
		enableCoach: true,
		tagIds: [],
		fixedWhatsappId: null,
		fixedQueueId: null,
	};

	const [contact, setContact] = useState(initialState);
	// Etiquetas da empresa, para montar a carteira desta pessoa.
	const [etiquetas, setEtiquetas] = useState([]);
	// Campos extras são exceção: quase ninguém usa, e em aberto eles roubam a
	// atenção de nome e número, que é o que importa.
	const [extrasAbertos, setExtrasAbertos] = useState(false);
	// Conexões e filas para o bloco de grupo. Só são buscadas quando a janela
	// abre — quem cadastra contato comum não paga por elas.
	const [filas, setFilas] = useState([]);
	// Conexão e fila fixas ficam em estado próprio, como o cadastro de usuário
	// faz com cargo e conexão. Pelo Formik os dois não chegavam ao campo: o
	// formulário nasce antes de o contato ser buscado, e estes campos não
	// acompanhavam a reinicialização — a tela abria "Sem conexão fixa" mesmo
	// com a conexão salva.
	const [fixaConexao, setFixaConexao] = useState("");
	const [fixaFila, setFixaFila] = useState("");
	const { whatsApps } = useWhatsApps();
	const ehGrupo = Boolean(contact?.isGroup);

	useEffect(() => {
		if (!open || !ehGrupo) return undefined;

		const ativo = { current: true };

		api
			.get("/queue")
			.then(({ data }) => {
				if (ativo.current) setFilas(Array.isArray(data) ? data : []);
			})
			.catch(() => undefined);

		return () => {
			ativo.current = false;
		};
	}, [open, ehGrupo]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				setContact(prevState => {
					return { ...prevState, ...initialValues };
				});
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					// O formulário grava ids; a API devolve as etiquetas inteiras,
					// porque a lista precisa da cor e do nome.
					setContact({
						...data,
						enableCoach: data.enableCoach !== undefined ? data.enableCoach : true,
						tagIds: (data.tags || []).map((tag) => tag.id),
					});
					setFixaConexao(
						data.fixedWhatsappId ? String(data.fixedWhatsappId) : "",
					);
					setFixaFila(data.fixedQueueId ? String(data.fixedQueueId) : "");
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	useEffect(() => {
		if (!open) return;
		(async () => {
			try {
				const { data } = await api.get("/tags");
				if (isMounted.current) setEtiquetas(Array.isArray(data) ? data : []);
			} catch (err) {
				// Sem etiquetas cadastradas o campo some; o resto do cadastro segue.
			}
		})();
	}, [open]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
	};

	const handleSaveContact = async values => {
		// Os dois campos do grupo vêm do estado, não do Formik. `null` desfixa
		// de propósito — é assim que o backend distingue "não mexi" de "tirei".
		const dados = {
			...values,
			fixedWhatsappId: fixaConexao ? Number(fixaConexao) : null,
			fixedQueueId: fixaFila ? Number(fixaFila) : null,
		};

		try {
			if (contactId) {
				await api.put(`/contacts/${contactId}`, dados);
				handleClose();
			} else {
				const { data } = await api.post("/contacts", dados);
				if (onSave) {
					onSave(data);
				}
				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					{/* Grupo e pessoa se editam na mesma janela, mas não são a
					    mesma coisa: o grupo tem um id do WhatsApp no lugar do
					    telefone e traz a configuração de conexão e fila. Dizer
					    qual dos dois está aberto evita o susto de ver "Número do
					    WhatsApp" com vinte dígitos. */}
					<DialogTitle className="flex items-center gap-2">
						{ehGrupo && <Users2 className="h-4 w-4 text-muted-foreground" />}
						{ehGrupo
							? "Editar grupo"
							: contactId
								? "Editar contato"
								: "Novo contato"}
						{ehGrupo && (
							<span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
								Grupo
							</span>
						)}
					</DialogTitle>
					{/* Alinhamento Kanban ↔ Contato: quando abrimos um contato já
					    salvo (novo contato não tem id nem cartão) mostramos em
					    qual etapa do funil ele está. Grupo não é trabalho
					    individual, então não mostra. */}
					{contactId && !ehGrupo && (
						<div className="-mt-1 flex flex-wrap items-center gap-2">
							<KanbanEtapaChip
								contactId={contactId}
								size="sm"
								showTitle
								mostrarVazio={true}
							/>
						</div>
					)}
					<DialogDescription>
						{ehGrupo
							? "Muda o cadastro do grupo na sua agenda. Ninguém no grupo é avisado e nada é enviado para lá."
							: contactId
								? "Muda só o cadastro na sua agenda. O contato não é avisado e nada é enviado para ele."
								: "Cadastrar aqui não avisa ninguém e não manda mensagem nenhuma. Só deixa a pessoa pronta na agenda, para você abrir a conversa quando quiser."}
					</DialogDescription>
				</DialogHeader>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting, setFieldValue }) => (
						<Form className="space-y-4">
							<div className="flex gap-3">
								<div className="flex-1 space-y-1.5">
									<Label htmlFor="name">{i18n.t("contactModal.form.name")}</Label>
									<Field
										as={Input}
										id="name"
										name="name"
										autoFocus
										placeholder="Ex.: Maria Silva"
									/>
									{touched.name && errors.name && (
										<p className="text-xs text-destructive">{errors.name}</p>
									)}
									<p className="text-xs leading-relaxed text-muted-foreground">
										É o que a equipe vê na lista de conversas.
									</p>
								</div>
								<div className="flex-1 space-y-1.5">
									{/* Grupo não tem telefone: o identificador é um número
									    longo do próprio WhatsApp. No seletor de país ele
									    ganhava uma bandeira inventada e virava um telefone
									    que não existe. */}
									<Label htmlFor="number">
										{ehGrupo
											? "ID do grupo"
											: i18n.t("contactModal.form.number")}
									</Label>

									{ehGrupo ? (
										<Field as={Input} id="number" name="number" readOnly />
									) : (
										<PhoneInput
											id="number"
											name="number"
											paisPadrao={regras.pais}
											value={values.number}
											onChange={(numero) => setFieldValue("number", numero)}
										/>
									)}

									{touched.number && errors.number && (
										<p className="text-xs text-destructive">{errors.number}</p>
									)}
									<p className="text-xs leading-relaxed text-muted-foreground">
										{ehGrupo
											? "Vem do WhatsApp e não pode ser alterado — é ele que identifica o grupo."
											: "Escolha o país ao lado e digite só o número local. Colar o número completo, com +, também funciona."}
									</p>
								</div>
							</div>

							{/* Contato sem número existe no banco e não serve para nada:
							    não dá para abrir conversa com ele. */}
							{!values.number && (
								<p className="flex gap-1.5 rounded-md bg-muted p-2 text-xs leading-relaxed">
									<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
									Sem o número do WhatsApp o contato fica só na agenda: não dá
									para abrir conversa com ele nem incluí-lo em disparos.
								</p>
							)}

							<div className="space-y-1.5">
								<Label htmlFor="email">{i18n.t("contactModal.form.email")}</Label>
								<Field
									as={Input}
									id="email"
									name="email"
									placeholder="maria@empresa.com.br"
								/>
								{touched.email && errors.email && (
									<p className="text-xs text-destructive">{errors.email}</p>
								)}
								<p className="text-xs leading-relaxed text-muted-foreground">
									Opcional. Fica guardado como referência — o sistema não manda
									e-mail.
								</p>
							</div>

							{etiquetas.length > 0 && (
								<div className="space-y-1.5">
									<Label>Etiquetas deste contato</Label>
									<MultiSelect
										options={etiquetas.map((tag) => ({
											value: String(tag.id),
											label: tag.name,
											color: tag.color,
										}))}
										value={(values.tagIds || []).map(String)}
										onChange={(ids) =>
											setFieldValue(
												"tagIds",
												ids.map((id) => Number(id)),
											)
										}
										placeholder="Nenhuma etiqueta"
									/>
									<p className="text-xs leading-relaxed text-muted-foreground">
										Ficam na pessoa, não na conversa: servem para separar a
										carteira de cada atendente e para escolher quem recebe uma
										campanha.
									</p>
								</div>
							)}

							{/* O backend já respeitava esta preferência (helpers/BotPause),
							    mas não havia como ligá-la: nem por tela, nem pela API. */}
							<div className="space-y-3 rounded-lg border p-3">
								<p className="text-sm font-semibold">Preferências do contato</p>
								<div className="flex items-center justify-between">
									<div>
										<Label htmlFor="enableCoach" className="text-sm">
											Habilitar Coach IA
										</Label>
										<p className="text-xs text-muted-foreground">
											Marcação só tem efeito prático quando "Apenas contatos
											selecionados" está ativo em Talk.Ai › Coach IA. O botão
											manual ⟳ SEMPRE funciona.
										</p>
									</div>
									<Switch
										id="enableCoach"
										checked={Boolean(values.enableCoach)}
										onCheckedChange={(marcado) =>
											setFieldValue("enableCoach", marcado)
										}
										className="ml-3"
									/>
								</div>
								<div className="flex items-center justify-between">
									<div className="space-y-0.5">
										<Label htmlFor="disableBot" className="text-sm">
											Não enviar mensagens automáticas
										</Label>
										<p className="text-xs leading-relaxed text-muted-foreground">
											Cala o robô para esta pessoa em qualquer atendimento:
											nada de chatbot, fluxo ou mensagem de ausência. A equipe
											continua conversando normalmente.
										</p>
									</div>
									<Switch
										id="disableBot"
										checked={Boolean(values.disableBot)}
										onCheckedChange={(marcado) =>
											setFieldValue("disableBot", marcado)
										}
									/>
								</div>
							</div>

							{/* Grupo com mais de um número da empresa dentro dele abria
							    um atendimento por número: a mesma conversa duplicada na
							    lista, e a equipe respondendo em paralelo sem saber. */}
							{ehGrupo && (
								<div className="space-y-3 rounded-lg border p-3">
									<div className="space-y-0.5">
										<Label className="text-sm">
											Fixar atendimento do grupo
										</Label>
										<p className="text-xs leading-relaxed text-muted-foreground">
											Se o grupo tiver mais de uma conexão sua, fixe qual
											atende — evita conversas duplicadas. A fila define quem
											recebe o aviso.
										</p>
									</div>

									<div className="grid gap-3 sm:grid-cols-2">
										<div className="space-y-1.5">
											<Label htmlFor="fixedWhatsappId" className="text-xs">
												Conexão fixa
											</Label>
											<Select
												value={fixaConexao || "none"}
												onValueChange={(v) =>
													setFixaConexao(v === "none" ? "" : v)
												}
											>
												<SelectTrigger id="fixedWhatsappId">
													{/* O nome vai como conteúdo, e não só o `value`: a
													    lista de conexões chega depois do formulário, e
													    o Radix mostra o texto padrão quando ainda não
													    encontra o item escolhido — ficava "Sem conexão
													    fixa" mesmo com a conexão salva. */}
													<SelectValue placeholder="Sem conexão fixa">
														{fixaConexao
															? whatsApps.find(
																	(c) => String(c.id) === fixaConexao,
																)?.name || "Conexão configurada"
															: "Sem conexão fixa"}
													</SelectValue>
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="none">
														Sem conexão fixa
													</SelectItem>
													{whatsApps.map((conexao) => (
														<SelectItem
															key={conexao.id}
															value={String(conexao.id)}
														>
															{conexao.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>

										<div className="space-y-1.5">
											<Label htmlFor="fixedQueueId" className="text-xs">
												Fila fixa
											</Label>
											<Select
												value={fixaFila || "none"}
												onValueChange={(v) =>
													setFixaFila(v === "none" ? "" : v)
												}
											>
												<SelectTrigger id="fixedQueueId">
													<SelectValue placeholder="Sem fila fixa">
														{fixaFila
															? filas.find((f) => String(f.id) === fixaFila)
																	?.name || "Fila configurada"
															: "Sem fila fixa"}
													</SelectValue>
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="none">Sem fila fixa</SelectItem>
													{filas.map((fila) => (
														<SelectItem key={fila.id} value={String(fila.id)}>
															{fila.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									</div>
								</div>
							)}

							<Secao
								titulo="Outras informações deste contato"
								resumo={
									values.extraInfo?.length
										? `${values.extraInfo.length} ${
												values.extraInfo.length === 1
													? "informação guardada"
													: "informações guardadas"
										  }`
										: "Nenhuma informação extra"
								}
								icone={ListPlus}
								aberta={extrasAbertos}
								onToggle={() => setExtrasAbertos((v) => !v)}
							>
								<p className="text-xs leading-relaxed text-muted-foreground">
									Anotações suas sobre a pessoa — documento, empresa, número do
									pedido. Aparecem para a equipe ao abrir o contato e não são
									enviadas para ninguém.
								</p>

								<FieldArray name="extraInfo">
								{({ push, remove }) => (
									<div className="space-y-2">
										{values.extraInfo &&
											values.extraInfo.length > 0 &&
											values.extraInfo.map((info, index) => (
												<div key={`${index}-info`} className="flex items-center gap-2">
													<Field
														as={Input}
														placeholder="Ex.: Empresa"
														name={`extraInfo[${index}].name`}
													/>
													<Field
														as={Input}
														placeholder="Ex.: Padaria do Zé"
														name={`extraInfo[${index}].value`}
													/>
													<Button
														type="button"
														variant="ghost"
														size="icon"
														onClick={() => remove(index)}
													>
														<Trash2 className="h-4 w-4" />
													</Button>
												</div>
											))}
										<Button
											type="button"
											variant="outline"
											size="sm"
											className="w-full"
											onClick={() => push({ name: "", value: "" })}
										>
											<Plus className="h-3.5 w-3.5" />
											{i18n.t("contactModal.buttons.addExtraInfo")}
										</Button>
									</div>
								)}
								</FieldArray>
							</Secao>

							<DialogFooter>
								<Button
									type="button"
									variant="outline"
									onClick={handleClose}
									disabled={isSubmitting}
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button type="submit" disabled={isSubmitting}>
									{isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
									{contactId ? "Salvar alterações" : "Criar contato"}
								</Button>
							</DialogFooter>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

export default ContactModal;
